import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { clone as cloneSkinned } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { MeshBVH } from 'three-mesh-bvh';
import { m } from './site';

// People on the masterplan's paving: some walking the promenades, some standing.
//
// These are skinned characters, so unlike the palms they cannot be instanced —
// InstancedMesh has no skinning path. Each person is a real clone with its own
// skeleton and AnimationMixer, which is why the count is in the dozens rather
// than the hundreds. SkeletonUtils.clone() is required rather than Object3D
// clone(): the latter copies the mesh but leaves every copy bound to the
// ORIGINAL skeleton, so all of them would move as one puppet.
//
// Placement is by rejection sampling against the model's own paving: a candidate
// is kept only where a downward ray hits near-level ground inside the plot and
// nothing is directly overhead. That is what keeps people out of the pool, off
// the model's cut edge and out of building interiors, without hand-authoring a
// single path.
//
// Everything here works in WORLD units, and the crowd is parented to the scene
// rather than to the model. Parenting to the model reads better but puts the
// people in a frame scaled by 5, where the metre helper m() — which returns
// world units — is wrong by exactly that factor: every sample landed five times
// outside the plot and not one person was placed.

const CHARACTERS = [
    'thobe_man2.glb',
    'hijab_woman.glb',
    'polo_man.glb',
    'graysuit_man.glb',
    'trenchcoat_woman.glb',
];

const PEOPLE_COUNT = 120;
// Base is one mesh per surface type, so the walkable set is an allowlist of
// materials rather than a geometric guess. Base_PoolFloor and
// Base_CrossSection are both dead level and would otherwise pass the slope
// test, putting people on the bottom of the pool and out on the maquette's cut
// edge; Grass is level too, but the brief was sidewalks.
//
// The test is on the NEAREST Base surface, not the nearest walkable one. The
// lawns and garden beds are planes laid over the paving, so skipping past them
// to the paving underneath seats people below the grass with their feet buried.
const WALKABLE_MATERIALS = [
    'Base_Cobblestone',
    'Base_FloorMosaic',
    'Base_Asphalt',
    'Base_SmallYard',
    'Base_red',
];
/** Share that walk rather than stand. */
const WALKER_RATIO = 0.62;
const EYE_HEIGHT_M = 1.72;
// Entourage exaggeration. True 1.7 m people on a 420 m maquette viewed from a
// drone camera read as specks; architectural models oversize their figures for
// exactly this reason. Applied to people and mirrored by the car in traffic.ts
// — keep the two in step or the street stops agreeing with itself.
const ENTOURAGE_SCALE = 1.4;
const WALK_SPEED_MS = 1.25;
// Ground counts as walkable only if it is near-level; this is what rejects
// roofs' sloped trim, planting beds and stair nosings.
const MIN_GROUND_NORMAL_Y = 0.94;
// A walker turns when the ground ahead stops being walkable.
const LOOKAHEAD_M = 4;
// Paving runs on under the buildings, so a level hit alone does not mean
// outdoors: a candidate with one of the building masses overhead is indoors.
// Only the masses are tested, not Portals or Elements, so people still walk
// under the archways and pergolas.
const OVERHEAD_GROUPS = ['Building_A', 'Building_B', 'Building_C'];
const HEADROOM_M = 14;
// Walkers re-probe the ground every few frames rather than every frame: at this
// scale the paving does not change fast enough to notice, and it keeps the
// per-frame raycast count down with a crowd this size.
const PROBE_INTERVAL = 5;

interface Person {
    root: THREE.Object3D;
    mixer: THREE.AnimationMixer;
    walking: boolean;
    heading: number;
    speed: number;
    /** Metres of paving still to cover before considering a new heading. */
    legRemaining: number;
    /** Distance from the ground to the root's origin, so feet stay planted. */
    footOffset: number;
    /** Frames until this walker re-probes the paving. */
    probeIn: number;
}

export interface PedestrianRig {
    readonly count: number;
    setVisible(visible: boolean): void;
    update(dt: number): void;
    dispose(): void;
}

export function createPedestrians(opts: {
    baseUrl: string;
    scene: THREE.Scene;
    /** The masterplan, searched for its Base group to stand people on. */
    model: THREE.Object3D;
    /** Half-extents of the plot in metres, for sampling. */
    plotHalfXM: number;
    plotHalfZM: number;
    /** How many figures to place. Defaults to the full desktop crowd. */
    count?: number;
}): PedestrianRig {
    const { baseUrl, scene, model, plotHalfXM, plotHalfZM } = opts;
    // Each figure is a skinned mesh with its own skeleton and animation mixer,
    // so the crowd is billed per head on the CPU every frame and cannot be
    // instanced away. Phones get a smaller one.
    const peopleCount = opts.count ?? PEOPLE_COUNT;

    const group = new THREE.Group();
    group.name = 'pedestrians';
    scene.add(group);

    const people: Person[] = [];
    const raycaster = new THREE.Raycaster();
    const down = new THREE.Vector3(0, -1, 0);
    const up = new THREE.Vector3(0, 1, 0);
    const probe = new THREE.Vector3();
    const worldNormal = new THREE.Vector3();
    const normalMatrix = new THREE.Matrix3();
    let groundTargets: THREE.Object3D[] = [];
    let overheadTargets: THREE.Object3D[] = [];
    // Two streams: placement must be reproducible run to run, and it would not
    // be if the render loop drew from the same sequence.
    const rng = makeRandom(31337);
    const wander = makeRandom(902113);

    const withBVH = (mesh: THREE.Mesh) => {
        // A crowd this size probes the ground thousands of times a second;
        // without a BVH those are full triangle scans.
        const geometry = mesh.geometry as THREE.BufferGeometry & { boundsTree?: MeshBVH };
        if (!geometry.boundsTree) geometry.boundsTree = new MeshBVH(geometry);
    };

    const collectGround = () => {
        groundTargets = [];
        const base = model.getObjectByName('Base');
        base?.traverse((child) => {
            const mesh = child as THREE.Mesh;
            if (!mesh.isMesh) return;
            withBVH(mesh);
            groundTargets.push(mesh);
        });

        overheadTargets = [];
        for (const name of OVERHEAD_GROUPS) {
            const group = model.getObjectByName(name);
            group?.traverse((child) => {
                const mesh = child as THREE.Mesh;
                if (!mesh.isMesh) return;
                withBVH(mesh);
                overheadTargets.push(mesh);
            });
        }
    };

    /** True if a slab or canopy sits over x/z at ground height y: indoors. */
    const isIndoors = (x: number, y: number, z: number): boolean => {
        probe.set(x, y + m(2.2), z);
        raycaster.set(probe, up);
        raycaster.far = m(HEADROOM_M);
        return raycaster.intersectObjects(overheadTargets, true).length > 0;
    };

    /** Walkable surface height in WORLD units at x/z, or null. */
    const groundAt = (x: number, z: number): number | null => {
        if (!groundTargets.length) return null;
        // Start above the plot's own high point and search down through it.
        probe.set(x, m(120), z);
        raycaster.set(probe, down);
        raycaster.far = m(300);
        const hit = raycaster.intersectObjects(groundTargets, true)[0];
        const normal = hit?.face?.normal;
        if (!normal) return null;

        const material = hit.object as unknown as { material?: THREE.Material };
        if (!material.material || !WALKABLE_MATERIALS.includes(material.material.name)) return null;

        // The normal MUST be taken into world space before its y means anything.
        // hit.face.normal is in the mesh's local frame, and this model's Base is
        // authored Z-up, so level paving reports y = 0 and z = 1 locally.
        // Testing the local y rejected every single candidate and placed nobody
        // at all.
        normalMatrix.getNormalMatrix(hit.object.matrixWorld);
        worldNormal.copy(normal).applyMatrix3(normalMatrix).normalize();
        return worldNormal.y >= MIN_GROUND_NORMAL_Y ? hit.point.y : null;
    };

    const loader = new GLTFLoader();
    let loaded = 0;

    CHARACTERS.forEach((file, index) => {
        loader.load(
            baseUrl + file,
            (gltf) => {
                const source = gltf.scene;
                const clip = gltf.animations[0];
                if (!clip) return;

                // Normalise height off the bounding box: the characters are not
                // authored to a shared scale, and a pedestrian that is even 20%
                // wrong next to a doorway is immediately obvious.
                const box = new THREE.Box3().setFromObject(source);
                const nativeHeight = Math.max(box.max.y - box.min.y, 1e-6);

                if (!groundTargets.length) collectGround();

                // Each character carries its share of the crowd.
                const share = Math.round(peopleCount / CHARACTERS.length);
                let placed = 0;
                let guard = 0;
                while (placed < share && guard++ < share * 60) {
                    const x = (rng() * 2 - 1) * m(plotHalfXM * 0.92);
                    const z = (rng() * 2 - 1) * m(plotHalfZM * 0.88);
                    const y = groundAt(x, z);
                    if (y === null) continue;
                    if (isIndoors(x, y, z)) continue;

                    const root = cloneSkinned(source);
                    const heightM = EYE_HEIGHT_M * ENTOURAGE_SCALE * (0.93 + rng() * 0.14);
                    const scale = m(heightM) / nativeHeight;
                    root.scale.setScalar(scale);
                    // Sit the feet on the paving: the bbox min is the sole.
                    root.position.set(x, y - box.min.y * scale, z);
                    // (that offset is stored below as footOffset)
                    const heading = rng() * Math.PI * 2;
                    root.rotation.y = heading;
                    root.traverse((child) => {
                        const mesh = child as THREE.Mesh;
                        if (mesh.isMesh) {
                            mesh.castShadow = false;
                            mesh.receiveShadow = false;
                            // People are small on screen and the plot is dense;
                            // per-person shadows cost more than they show.
                            mesh.frustumCulled = true;
                        }
                    });
                    group.add(root);

                    const mixer = new THREE.AnimationMixer(root);
                    const action = mixer.clipAction(clip);
                    action.play();
                    // Offset into the cycle so thirty people are not in lockstep.
                    action.time = rng() * clip.duration;

                    const walking = rng() < WALKER_RATIO;
                    if (!walking) {
                        // There is no idle clip in the pack, only walking. Held
                        // at a frame the rig is frozen mid-stride, which reads as
                        // a mannequin; run at a crawl instead and it reads as
                        // someone standing and shifting their weight.
                        action.timeScale = 0.05 + rng() * 0.05;
                    } else {
                        action.timeScale = 0.85 + rng() * 0.35;
                    }

                    people.push({
                        root,
                        mixer,
                        walking,
                        heading,
                        speed: WALK_SPEED_MS * (0.8 + rng() * 0.45),
                        legRemaining: 6 + rng() * 22,
                        footOffset: -box.min.y * scale,
                        // Stagger the probes so the whole crowd does not raycast
                        // on the same frame.
                        probeIn: Math.floor(rng() * PROBE_INTERVAL),
                    });
                    placed++;
                }
                loaded++;
                if (loaded === CHARACTERS.length) {
                    console.info(
                        `[people] ${people.length} pedestrians placed (${people.filter((p) => p.walking).length} walking)`,
                    );
                }
            },
            undefined,
            (err) => console.error(`Failed to load ${file}:`, err),
        );
        void index;
    });

    return {
        get count() {
            return people.length;
        },
        setVisible(visible) {
            group.visible = visible;
        },
        update(dt) {
            if (!group.visible) return;
            const step = Math.min(dt, 0.1);
            for (const person of people) {
                person.mixer.update(step);
                if (!person.walking) continue;

                const distance = m(person.speed) * step;
                const nx = person.root.position.x + Math.sin(person.heading) * distance;
                const nz = person.root.position.z + Math.cos(person.heading) * distance;
                person.legRemaining -= person.speed * step;

                // Move freely between probes and only test the paving every few
                // frames; a metre of travel cannot cross a kerb the lookahead
                // has not already seen.
                if (person.probeIn-- > 0) {
                    person.root.position.x = nx;
                    person.root.position.z = nz;
                    continue;
                }
                person.probeIn = PROBE_INTERVAL;

                // Probe a few metres ahead rather than at the feet, so a turn
                // begins before the kerb instead of on top of it.
                const aheadX = person.root.position.x + Math.sin(person.heading) * m(LOOKAHEAD_M);
                const aheadZ = person.root.position.z + Math.cos(person.heading) * m(LOOKAHEAD_M);
                const ahead = groundAt(aheadX, aheadZ);

                if (ahead === null || person.legRemaining <= 0) {
                    // Turn away rather than reverse: a crowd all doing
                    // about-faces at the same kerb looks mechanical.
                    person.heading +=
                        (Math.PI * 0.5 + wander() * Math.PI) * (wander() < 0.5 ? -1 : 1);
                    person.root.rotation.y = person.heading;
                    person.legRemaining = 6 + wander() * 22;
                    continue;
                }

                const y = groundAt(nx, nz);
                if (y === null) continue;
                // Feet stay planted as the paving steps and ramps. The offset is
                // the character's own sole-to-origin distance, kept from
                // placement; recomputing it from the current position collapses
                // to a no-op and the person slowly sinks or floats.
                person.root.position.set(nx, y + person.footOffset, nz);
            }
        },
        dispose() {
            people.forEach((p) => p.mixer.stopAllAction());
            scene.remove(group);
        },
    };
}

/** Deterministic pseudo-random, so the crowd is identical every load. */
function makeRandom(seed: number): () => number {
    let s = seed >>> 0;
    return () => {
        s = (s * 1664525 + 1013904223) >>> 0;
        return s / 4294967296;
    };
}
